import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Plus, GripVertical, Trash2, Eye, Copy, ArrowUp, ArrowDown, Activity } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File Upload' },
];

export default function SurveyBuilderPage() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [showSummary, setShowSummary] = useState(true);
  const [fields, setFields] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchSurveys = async () => {
    try {
      const res = await API.get('/surveys');
      setSurveys(res.data.surveys || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSurveys(); }, []);

  const addField = () => {
    setFields([...fields, {
      id: `field_${Date.now()}`,
      type: 'text',
      label: '',
      required: false,
      placeholder: '',
      options: [],
      order: fields.length
    }]);
  };

  const updateField = (index, updates) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };
    setFields(updated);
  };

  const removeField = (index) => setFields(fields.filter((_, i) => i !== index));

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    const updated = [...fields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFields(updated);
  };

  const saveSurvey = async () => {
    if (!newName.trim()) { toast.error('Survey name is required'); return; }
    try {
      const data = { name: newName, description: newDesc, fields, show_summary_to_patient: showSummary };
      if (editingSurvey) {
        await API.put(`/surveys/${editingSurvey.id}`, data);
        toast.success('Survey updated');
      } else {
        await API.post('/surveys', data);
        toast.success('Survey created');
      }
      setCreateOpen(false);
      resetForm();
      fetchSurveys();
    } catch (err) { toast.error('Failed to save survey'); }
  };

  const resetForm = () => {
    setEditingSurvey(null);
    setNewName('');
    setNewDesc('');
    setFields([]);
    setShowSummary(true);
  };

  const editSurvey = (survey) => {
    setEditingSurvey(survey);
    setNewName(survey.name);
    setNewDesc(survey.description || '');
    setFields(survey.fields || []);
    setShowSummary(survey.show_summary_to_patient !== false);
    setCreateOpen(true);
  };

  const copyLink = (survey) => {
    const url = `${window.location.origin}/forms/public/${survey.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Form link copied to clipboard!');
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 data-testid="surveys-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Pre-Visit Forms</h1>
          <p className="text-slate-500 mt-1">Create and manage patient intake forms</p>
        </div>
        <Button data-testid="create-survey-btn" onClick={() => { resetForm(); setCreateOpen(true); }} className="bg-sky-600 hover:bg-sky-700">
          <Plus className="w-4 h-4 mr-2" /> Create Form
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="surveys-grid">
        {surveys.map(survey => (
          <Card key={survey.id} data-testid={`survey-card-${survey.id}`} className="border-slate-200 hover:border-sky-200 transition-all">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  <h3 className="font-semibold text-slate-900">{survey.name}</h3>
                </div>
                <Badge className={survey.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{survey.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
              {survey.description && <p className="text-sm text-slate-500 mb-3">{survey.description}</p>}
              <p className="text-xs text-slate-400 mb-3">{(survey.fields || []).length} fields</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => editSurvey(survey)} data-testid={`edit-survey-${survey.id}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => copyLink(survey)} data-testid={`copy-link-${survey.id}`}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setFields(survey.fields || []); setPreviewOpen(true); }}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {surveys.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No forms created yet</p>
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{editingSurvey ? 'Edit Form' : 'Create New Form'}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Form Name</Label><Input data-testid="survey-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Pre-Visit Questionnaire" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Brief description..." /></div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium">Show Summary to Patient</p>
                <p className="text-xs text-slate-500">Display collected info summary after submission</p>
              </div>
              <Switch checked={showSummary} onCheckedChange={setShowSummary} data-testid="show-summary-toggle" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Form Fields</Label>
                <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Field</Button>
              </div>
              {fields.map((field, idx) => (
                <div key={field.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3" data-testid={`field-${idx}`}>
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-500">Field {idx + 1}</span>
                    <div className="flex-1" />
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveField(idx, -1)} disabled={idx === 0}><ArrowUp className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1}><ArrowDown className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeField(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select value={field.type} onValueChange={v => updateField(idx, { type: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input className="h-9" value={field.label} onChange={e => updateField(idx, { label: e.target.value })} placeholder="Question label" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Placeholder</Label>
                      <Input className="h-9" value={field.placeholder || ''} onChange={e => updateField(idx, { placeholder: e.target.value })} placeholder="Placeholder text" />
                    </div>
                  </div>
                  {['select', 'radio', 'checkbox'].includes(field.type) && (
                    <div className="space-y-1">
                      <Label className="text-xs">Options (comma-separated)</Label>
                      <Input className="h-9" value={(field.options || []).join(', ')} onChange={e => updateField(idx, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })} placeholder="Option 1, Option 2, Option 3" />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch checked={field.required} onCheckedChange={v => updateField(idx, { required: v })} />
                    <Label className="text-xs">Required</Label>
                  </div>
                </div>
              ))}
            </div>

            <Button data-testid="save-survey-btn" onClick={saveSurvey} className="w-full bg-sky-600 hover:bg-sky-700">
              {editingSurvey ? 'Update Form' : 'Create Form'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Form Preview</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {fields.map((field, idx) => (
              <div key={idx} className="space-y-1.5">
                <Label className="text-sm">{field.label || `Field ${idx + 1}`} {field.required && <span className="text-red-500">*</span>}</Label>
                {field.type === 'textarea' ? <Textarea placeholder={field.placeholder} rows={3} disabled /> :
                 field.type === 'select' ? (
                   <Select disabled><SelectTrigger><SelectValue placeholder={field.placeholder || 'Select...'} /></SelectTrigger></Select>
                 ) : field.type === 'radio' ? (
                   <div className="space-y-1">{(field.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="radio" disabled />{o}</label>)}</div>
                 ) : field.type === 'checkbox' ? (
                   <div className="space-y-1">{(field.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled />{o}</label>)}</div>
                 ) : <Input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'} placeholder={field.placeholder} disabled />}
              </div>
            ))}
            {fields.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No fields added yet</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
